import React, { useState, useEffect } from 'react';
import adminAxios from '../utils/axiosConfig';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Search, 
  Send, 
  Trash2, 
  TrendingUp,
  Euro,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchQuotes();
    fetchStats();
  }, []);

  useEffect(() => {
    filterQuotes();
  }, [searchTerm, filterStatus, quotes]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get('/admin/quotes');
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAxios.get('/admin/quotes/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterQuotes = () => {
    let filtered = [...quotes];
    
    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(q => q.status === filterStatus);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.quoteNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredQuotes(filtered);
  };

  const handleResend = async (quoteId) => {
    try {
      await adminAxios.post(`/admin/quotes/${quoteId}/resend`);
      toast.success('Devis renvoyé avec succès');
    } catch (error) {
      console.error('Error resending quote:', error);
      toast.error('Erreur lors du renvoi du devis');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    
    try {
      await adminAxios.delete(`/admin/quotes/${quoteId}`);
      toast.success('Devis supprimé');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const viewDetails = async (quoteId) => {
    try {
      const response = await adminAxios.get(`/admin/quotes/${quoteId}`);
      setSelectedQuote(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching quote details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', icon: Clock },
      accepted: { label: 'Accepté', color: 'bg-green-500/20 text-green-300 border-green-500/30', icon: CheckCircle },
      declined: { label: 'Refusé', color: 'bg-red-500/20 text-red-300 border-red-500/30', icon: XCircle },
      expired: { label: 'Expiré', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };
  
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gray-800/50 border-gold/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Devis</p>
                  <p className="text-2xl font-bold text-white">{stats.totalQuotes}</p>
                </div>
                <FileText className="w-8 h-8 text-gold" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gold/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">En attente</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pendingQuotes}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gold/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Taux d&apos;acceptation</p>
                  <p className="text-2xl font-bold text-green-400">{stats.acceptanceRate}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gold/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Valeur acceptée</p>
                  <p className="text-2xl font-bold text-gold">{stats.acceptedValue.toFixed(0)}€</p>
                </div>
                <Euro className="w-8 h-8 text-gold" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex-1 max-w-md w-full">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email, numéro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-800/50 border-gold/30 text-white placeholder-gray-500"
            />
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('all')}
            className="bg-gold hover:bg-gold/90 text-black"
          >
            Tous
          </Button>
          <Button
            variant={filterStatus === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('pending')}
            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
          >
            En attente
          </Button>
          <Button
            variant={filterStatus === 'accepted' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('accepted')}
            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
          >
            Acceptés
          </Button>
          <Button
            variant={filterStatus === 'declined' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('declined')}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            Refusés
          </Button>
        </div>
      </div>
      
      {/* Quotes List */}
      <div className="space-y-4">
        {filteredQuotes.length === 0 ? (
          <Card className="bg-gray-800/50 border-gold/20">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">Aucun devis trouvé</p>
            </CardContent>
          </Card>
        ) : (
          filteredQuotes.map((quote) => (
            <Card key={quote.id} className="bg-gray-800/50 border-gold/20 hover:border-gold/40 transition-all">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">Devis #{quote.quoteNumber}</h3>
                      {getStatusBadge(quote.status)}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Client:</span>
                        <span className="text-white font-medium">{quote.customerName}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Email:</span>
                        <span className="text-white">{quote.customerEmail}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Téléphone:</span>
                        <span className="text-white">{quote.customerPhone}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Date:</span>
                        <span className="text-white">{formatDate(quote.pickupDate)} à {quote.pickupTime}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Départ:</span>
                        <span className="text-white">{quote.pickup}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Arrivée:</span>
                        <span className="text-white">{quote.dropoff}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Distance:</span>
                        <span className="text-white">{quote.distance} km</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 min-w-[100px]">Prix:</span>
                        <span className="text-2xl font-bold text-gold">{quote.totalPrice}€</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 justify-end pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewDetails(quote.id)}
                        className="border-gold/30 text-gold hover:bg-gold/10"
                      >
                        Détails
                      </Button>
                      
                      {quote.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResend(quote.id)}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Renvoyer
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={() => handleDelete(quote.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Details Modal */}
      {showDetailsModal && selectedQuote && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailsModal(false)}>
          <Card className="bg-gray-900 border-gold/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-white">Détails du Devis #{selectedQuote.quoteNumber}</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetailsModal(false)}
                  className="border-gold/30 text-gold hover:bg-gold/10"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Nom</label>
                    <p className="text-white font-medium">{selectedQuote.customerName}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Email</label>
                    <p className="text-white">{selectedQuote.customerEmail}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Téléphone</label>
                    <p className="text-white">{selectedQuote.customerPhone}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Date de départ</label>
                    <p className="text-white">{formatDate(selectedQuote.pickupDate)} à {selectedQuote.pickupTime}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-gray-400 text-sm">Départ</label>
                    <p className="text-white">{selectedQuote.pickup}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-gray-400 text-sm">Arrivée</label>
                    <p className="text-white">{selectedQuote.dropoff}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Distance</label>
                    <p className="text-white">{selectedQuote.distance} km</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Prix Total</label>
                    <p className="text-2xl font-bold text-gold">{selectedQuote.totalPrice}€</p>
                  </div>
                </div>
                
                {selectedQuote.notes && (
                  <div>
                    <label className="text-gray-400 text-sm">Notes</label>
                    <p className="text-white">{selectedQuote.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminQuotes;